/**
 * Create Inference Job Page
 *
 * Form to create a new inference job by selecting a trained model
 * and configuring detection parameters.
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  SelectItem,
  Slider,
  Spinner,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Brain, Settings, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useModels } from '@/lib/api/hooks/models';
import { useCreateInferenceJob } from '@/lib/api/hooks/inference';

const inferenceConfigSchema = z.object({
  model_id: z.string().min(1, 'Model is required'),
  threshold: z.number().min(0).max(1),
  merge_window_ms: z.number().min(0).max(5000),
  min_duration_ms: z.number().min(0).max(2000),
});

type InferenceFormData = z.infer<typeof inferenceConfigSchema>;

export const Route = createFileRoute('/inference/new')({
  component: NewInferenceJobPage,
});

function NewInferenceJobPage() {
  const navigate = useNavigate();
  const createInferenceJob = useCreateInferenceJob();
  const { data: models, isLoading: isLoadingModels } = useModels({
    filters: { is_active: true },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InferenceFormData>({
    resolver: zodResolver(inferenceConfigSchema),
    defaultValues: {
      model_id: '',
      threshold: 0.5,
      merge_window_ms: 500,
      min_duration_ms: 100,
    },
  });

  const selectedModelId = watch('model_id');
  const thresholdValue = watch('threshold');

  // Get selected model details
  const selectedModel = useMemo(
    () => models?.find((m) => m.id === selectedModelId),
    [models, selectedModelId]
  );

  const onSubmit = async (data: InferenceFormData) => {
    try {
      const result = await createInferenceJob.mutateAsync({
        model_id: data.model_id,
        config: {
          threshold: data.threshold,
          merge_window_ms: data.merge_window_ms,
          min_duration_ms: data.min_duration_ms,
        },
      });
      if (result?.id) {
        navigate({ to: '/inference/$jobId', params: { jobId: result.id } });
      }
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <div className="container-page max-w-2xl py-4">
      <div className="mb-6">
        <Button
          variant="light"
          size="sm"
          startContent={<ArrowLeft className="h-4 w-4" />}
          onPress={() => navigate({ to: '/inference' })}
          className="mb-4"
        >
          Back to Inference Jobs
        </Button>
        <h1>New Inference Job</h1>
        <p className="text-default-500">
          Run artifact detection on new audio files using a trained model.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Model Selection */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Brain className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <h2>Select Model</h2>
              <p className="text-xs text-default-500">
                Choose a trained model to use for detection
              </p>
            </div>
          </CardHeader>
          <CardBody className="space-y-3 px-4 pb-4 pt-2">
            {isLoadingModels ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : models && models.length > 0 ? (
              <Controller
                name="model_id"
                control={control}
                render={({ field }) => (
                  <Select
                    size="sm"
                    label="Model"
                    labelPlacement="outside"
                    placeholder="Select a model"
                    selectedKeys={field.value ? [field.value] : []}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string;
                      field.onChange(selected || '');
                    }}
                    isInvalid={!!errors.model_id}
                    errorMessage={errors.model_id?.message}
                    isRequired
                  >
                    {models.map((model) => (
                      <SelectItem key={model.id} textValue={model.name || `Model ${model.id.slice(0, 8)}`}>
                        <div className="flex flex-col">
                          <span className="text-sm">
                            {model.name || `Model ${model.id.slice(0, 8)}`}
                          </span>
                          <span className="text-xs text-default-400">
                            {model.artifact_types.join(', ')} •{' '}
                            {((model.model_size_bytes ?? 0) / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </Select>
                )}
              />
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-default-200 bg-default-50 p-4">
                <AlertCircle className="h-5 w-5 text-warning" />
                <div className="flex-1">
                  <p className="text-sm font-medium">No trained models found</p>
                  <p className="text-xs text-default-500">
                    Train a model first to run inference.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => navigate({ to: '/training/new' })}
                >
                  Train Model
                </Button>
              </div>
            )}

            {/* Selected Model Details */}
            {selectedModel && (
              <div className="rounded-lg bg-content2 p-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Zap className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {selectedModel.name || `Model ${selectedModel.id.slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-default-500">
                      {selectedModel.description || 'No description'}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {selectedModel.artifact_types.map((type) => (
                        <span
                          key={type}
                          className="inline-flex items-center rounded-full bg-default-100 px-2 py-0.5 text-xs text-default-600"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Detection Configuration */}
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-default-100">
              <Settings className="h-4 w-4 text-default-500" />
            </div>
            <div className="flex flex-col">
              <h2>Detection Settings</h2>
              <p className="text-xs text-default-500">
                Tune detection sensitivity and output filtering
              </p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6 px-4 pb-4 pt-2">
            {/* Threshold Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Detection Threshold</p>
                  <p className="text-xs text-default-500">
                    Higher = fewer false positives, lower = catch more artifacts
                  </p>
                </div>
                <span className="text-sm font-mono text-primary">
                  {thresholdValue.toFixed(2)}
                </span>
              </div>
              <Controller
                name="threshold"
                control={control}
                render={({ field }) => (
                  <Slider
                    size="sm"
                    step={0.05}
                    minValue={0}
                    maxValue={1}
                    value={field.value}
                    onChange={(value) => field.onChange(value as number)}
                    className="max-w-full"
                    classNames={{
                      track: 'h-2',
                      filler: 'bg-primary',
                    }}
                    marks={[
                      { value: 0, label: 'Sensitive' },
                      { value: 0.5, label: 'Balanced' },
                      { value: 1, label: 'Strict' },
                    ]}
                  />
                )}
              />
            </div>

            {/* Merge Window and Min Duration */}
            <div className="grid grid-cols-2 gap-3">
              <Controller
                name="merge_window_ms"
                control={control}
                render={({ field }) => (
                  <Input
                    size="sm"
                    type="number"
                    label="Merge Window (ms)"
                    labelPlacement="outside"
                    description="Combine detections within this gap"
                    value={field.value.toString()}
                    onValueChange={(value) => field.onChange(parseInt(value) || 0)}
                    isInvalid={!!errors.merge_window_ms}
                    errorMessage={errors.merge_window_ms?.message}
                  />
                )}
              />
              <Controller
                name="min_duration_ms"
                control={control}
                render={({ field }) => (
                  <Input
                    size="sm"
                    type="number"
                    label="Min Duration (ms)"
                    labelPlacement="outside"
                    description="Ignore detections shorter than this"
                    value={field.value.toString()}
                    onValueChange={(value) => field.onChange(parseInt(value) || 0)}
                    isInvalid={!!errors.min_duration_ms}
                    errorMessage={errors.min_duration_ms?.message}
                  />
                )}
              />
            </div>

            {/* Configuration Preview */}
            <div className="rounded-lg bg-default-50 p-3">
              <p className="text-xs text-default-500">
                Detections with confidence below {(thresholdValue * 100).toFixed(0)}% will be
                filtered. Adjacent detections within {watch('merge_window_ms')}ms will be merged.
                Detections shorter than {watch('min_duration_ms')}ms will be ignored.
              </p>
            </div>
          </CardBody>
        </Card>

        {/* What Happens Next */}
        <Card className="border border-primary/20 bg-primary/5 shadow-none">
          <CardBody className="px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Zap className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-primary">Next: Upload Audio Files</p>
                <p className="text-xs text-default-500">
                  After creating this job, you'll be able to upload audio files for processing.
                  Results will be available as soon as each file is analyzed.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="flat"
            size="sm"
            onPress={() => navigate({ to: '/inference' })}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            size="sm"
            isLoading={isSubmitting || createInferenceJob.isPending}
            isDisabled={!selectedModelId}
          >
            Create Job
          </Button>
        </div>
      </form>
    </div>
  );
}
